import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Ticket, MessageSquare, ThumbsDown, Circle } from "lucide-react";
import { toast } from "react-toastify";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from "./ui/dropdown-menu";
import SupportConfirmationModal from "../pages/support-system/SupportConfirmationModal";
import {
  getUnreadNotificationCount,
  getNotifications,
  markNotificationsRead,
  readApiError,
} from "../pages/support-system/supportApi";

// A badge should not cost a page of rows, which is why unread-count is its own endpoint. The
// admin limiter allows 100 requests a minute; this spends one.
const POLL_MS = 60_000;

/**
 * Only the three the backend actually raises today get their own icon. `integration.failed`
 * and `agent.inactive` are declared but nothing produces them, so they fall through to the
 * bell rather than getting UI built around a type that never arrives.
 */
const TYPE_ICONS = {
  "ticket.created": Ticket,
  "ticket.reply": Ticket,
  "chat.escalated": MessageSquare,
  "feedback.negative": ThumbsDown,
};

/**
 * The link a notification carries is panel-neutral — `/tickets/:id`, not one of our routes.
 * No page here reads an id out of the URL, so this lands on the right page and stops there.
 */
const routeFor = (link) => {
  if (!link) return null;
  if (link.startsWith("/tickets/") || link.startsWith("/chats/")) {
    return "/support-system/tickets";
  }
  if (link.startsWith("/articles/")) return "/support-system/articles-kb";
  return null;
};

const timeAgo = (date) => {
  const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
};

export default function SupportNotificationBell() {
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmingMarkAll, setConfirmingMarkAll] = useState(false);
  const [isMarkingAll, setIsMarkingAll] = useState(false);

  const refreshCount = useCallback(async () => {
    try {
      const data = await getUnreadNotificationCount();
      setUnread(data.unread ?? 0);
    } catch (error) {
      // A badge that failed to refresh is not worth a toast in the corner of every page.
      console.error("Error fetching unread count:", error);
    }
  }, []);

  useEffect(() => {
    refreshCount();
    const timer = setInterval(refreshCount, POLL_MS);
    return () => clearInterval(timer);
  }, [refreshCount]);

  // Read and unread together: a list that hides everything the moment it is read leaves the
  // panel looking empty right after someone has been working in it.
  const loadNotifications = async () => {
    setIsLoading(true);
    try {
      const data = await getNotifications("", 1, 20);
      setNotifications(data.notifications || []);
      setUnread(data.unread ?? 0);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      toast.error("Failed to load notifications");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenChange = (next) => {
    setOpen(next);
    if (next) loadNotifications();
  };

  const handleSelect = async (notification) => {
    setOpen(false);

    if (!notification.read) {
      // Optimistic: the row is already marked in the list the user is looking at, and a
      // failed write only means the badge is one too high until the next poll.
      setNotifications((prev) =>
        prev.map((n) => (n.id === notification.id ? { ...n, read: true } : n)),
      );
      setUnread((n) => Math.max(0, n - 1));
      markNotificationsRead([notification.id]).catch((error) =>
        console.error("Error marking notification read:", error),
      );
    }

    const route = routeFor(notification.link);
    if (route) navigate(route);
  };

  const handleMarkAll = async () => {
    setIsMarkingAll(true);
    try {
      const response = await markNotificationsRead();
      if (response.ok) {
        setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
        setUnread(0);
        setConfirmingMarkAll(false);
        return;
      }
      toast.error(await readApiError(response, "Could not mark them read"));
    } catch (error) {
      console.error("Error marking all read:", error);
      toast.error("Could not mark them read. Please try again.");
    } finally {
      setIsMarkingAll(false);
    }
  };

  return (
    <>
      <DropdownMenu open={open} onOpenChange={handleOpenChange}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="relative flex h-9 w-9 items-center justify-center rounded-md border border-border/70 bg-white/[0.02] text-muted-foreground transition-colors hover:bg-white/[0.05] hover:text-foreground"
            aria-label={
              unread > 0 ? `Notifications, ${unread} unread` : "Notifications"
            }
          >
            <Bell className="h-4 w-4" aria-hidden="true" />
            {unread > 0 && (
              <span className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-ginox-green px-1 text-[9px] font-semibold tabular-nums text-black">
                {unread > 99 ? "99+" : unread}
              </span>
            )}
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent className="w-[320px] p-0 sm:w-[380px]">
          <div className="flex items-start justify-between gap-3 border-b border-border/70 px-3 py-2.5">
            <div>
              <p className="text-xs font-semibold text-foreground">Notifications</p>
              {/* One row, one read flag — there is no per-admin state behind this. */}
              <p className="mt-0.5 text-[10px] text-muted-foreground">
                Shared with all admins.
              </p>
            </div>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  setConfirmingMarkAll(true);
                }}
                className="shrink-0 text-[10px] text-muted-foreground transition-colors hover:text-foreground"
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-[360px] overflow-y-auto">
            {isLoading ? (
              <div className="flex justify-center py-8">
                <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-ginox-green" />
              </div>
            ) : notifications.length === 0 ? (
              <p className="px-3 py-8 text-center text-xs text-muted-foreground">
                Nothing yet.
              </p>
            ) : (
              notifications.map((notification) => {
                const Icon = TYPE_ICONS[notification.type] || Bell;
                return (
                  <button
                    key={notification.id}
                    type="button"
                    onClick={() => handleSelect(notification)}
                    className="flex w-full items-start gap-2.5 border-b border-border/40 px-3 py-2.5 text-left transition-colors last:border-b-0 hover:bg-white/[0.04]"
                  >
                    <Icon
                      className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground"
                      aria-hidden="true"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-2">
                        <p
                          className={`truncate text-xs ${
                            notification.read
                              ? "text-muted-foreground"
                              : "font-medium text-foreground"
                          }`}
                        >
                          {notification.title}
                        </p>
                        <span className="shrink-0 font-mono text-[9px] text-muted-foreground/60">
                          {timeAgo(notification.createdAt)}
                        </span>
                      </div>
                      {/* Customer-written text in some of these — plain text only. */}
                      <p className="mt-0.5 line-clamp-2 text-[11px] text-muted-foreground">
                        {notification.body}
                      </p>
                    </div>
                    {!notification.read && (
                      <Circle
                        className="mt-1 h-2 w-2 shrink-0 fill-ginox-green text-ginox-green"
                        aria-label="Unread"
                      />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Nothing un-reads a notification, and this one button covers every admin's inbox. */}
      <SupportConfirmationModal
        isOpen={confirmingMarkAll}
        onClose={() => setConfirmingMarkAll(false)}
        onConfirm={handleMarkAll}
        title="Mark All Read"
        message={`Mark all ${unread} unread notification(s) as read? This cannot be undone, and it applies to every admin — the inbox is shared.`}
        confirmText="Yes, Mark All"
        cancelText="No, Cancel"
        loading={isMarkingAll}
        loadingText="Marking..."
      />
    </>
  );
}
